/**
 * Interface tables (ADR-111): which exist, and loading them.
 *
 * One directory per interface locale, `./locales/<code>/`:
 *   table.ts       the strings, typed against the English table
 *   formLabels.ts  the grammatical-form label map (D2)
 *   meta.ts        the review status, `ai-draft` or `native-reviewed`
 *
 * A locale has an interface table exactly when its directory has a table and a
 * meta: the globs below find them, and nothing else lists them. English is
 * bundled and is the fallback; every other table is its own chunk, loaded on
 * demand. The language facts (names, regional tag, release) are not here: they
 * are the backend registry, read through ./registry.ts.
 */
import { table as en } from './locales/en/table'

export type AppTranslations = typeof en

/** A language code that has an interface table (see `hasInterfaceTable`). */
export type AppLocale = string

export type ReviewStatus = 'ai-draft' | 'native-reviewed'

export interface InterfaceTableMeta {
  review: ReviewStatus
}

// './locales/ru/meta.ts' → 'ru'
const codeOf = (path: string): string => path.split('/')[2]

const META = import.meta.glob<InterfaceTableMeta>('./locales/*/meta.ts', {
  eager: true,
  import: 'meta',
})

// English is excluded: it is imported statically above, so a lazy entry for it
// would neither split it out nor be needed.
const LOADERS = import.meta.glob<AppTranslations>(
  ['./locales/*/table.ts', '!./locales/en/table.ts'],
  { import: 'table' },
)

/** Every locale with an interface table, and its review status. */
export const INTERFACE_TABLES: Readonly<Record<AppLocale, InterfaceTableMeta>> = Object.fromEntries(
  Object.entries(META)
    .map(([path, meta]) => [codeOf(path), meta] as const)
    .filter(([code]) => code === 'en' || `./locales/${code}/table.ts` in LOADERS),
)

/**
 * The tables loaded so far, by code. English is bundled and always present;
 * another appears once `loadInterfaceTable` has resolved for it.
 */
export const APP_TRANSLATIONS: { en: AppTranslations } & Partial<Record<AppLocale, AppTranslations>> = { en }

export function hasInterfaceTable(code: string | null | undefined): code is AppLocale {
  return !!code && code in INTERFACE_TABLES
}

/**
 * Resolve a stored interface_language to the locale to render: the code itself
 * when it has a table, else English. Release is deliberately not consulted: it
 * gates choosing a language, not honouring one an account already holds
 * (ADR-111 Amendment 1, clause 2).
 */
export function resolveLocale(code: string | null | undefined): AppLocale {
  return hasInterfaceTable(code) ? code : 'en'
}

const pending = new Map<AppLocale, Promise<AppTranslations>>()

/**
 * Load a locale's table (once; concurrent callers share the request). A code
 * with no table resolves to English. A failed chunk load rejects, and the next
 * call tries again.
 */
export function loadInterfaceTable(code: AppLocale): Promise<AppTranslations> {
  const loaded = APP_TRANSLATIONS[code]
  if (loaded) return Promise.resolve(loaded)
  const loader = hasInterfaceTable(code) ? LOADERS[`./locales/${code}/table.ts`] : undefined
  if (!loader) return Promise.resolve(en)
  let request = pending.get(code)
  if (!request) {
    request = loader()
      .then((table) => {
        APP_TRANSLATIONS[code] = table
        return table
      })
      .finally(() => pending.delete(code))
    pending.set(code, request)
  }
  return request
}
